/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { useAuth0 } from '@auth0/auth0-react'
import { t } from '@lingui/macro'
import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent, Switch } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AppointmentDTO } from '@om1/aspen-api/codegen/client/models/AppointmentDTO'
import { useStatusOptions } from '@om1/aspen-api/codegen/hooks/useStatusOptions'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { APPOINTMENT_STATUS_LABELS } from '../utils/appointment'
import { filterAllocatedStatusValues } from '../utils/candidate-status'
import { pickedDayEnd, pickedDayStart, pickerDateFromZonedIso } from '../utils/timezone'
import { StableSearchInput } from './StableSearchInput'
import { StatusBadge } from './StatusBadge'

export interface CandidateFilters {
    search?: string
    status?: string[]
    createdAfter?: string
    createdBefore?: string
    uncontactedOnly?: boolean
    hasPreferredContactTime?: boolean
    preferredContactAfter?: string
    preferredContactBefore?: string
    // Appointment filters (APP-1220). Deliberately separate from the
    // preferred-contact ones above: a candidate can have one, both or neither,
    // and a single set of controls could not express "booked, but not in this
    // range" versus "never booked at all".
    appointmentAfter?: string
    appointmentBefore?: string
    appointmentStatus?: string[]
    hasAppointment?: boolean
}

interface CandidateFilterComponentProps {
    isOpen?: boolean
    onFiltersChange: (filters: CandidateFilters) => void
    currentFilters?: CandidateFilters
    onSearchChange?: (search: string) => void
    currentSearch?: string
}

type AppointmentStatusKey = AppointmentDTO['status']

/**
 * Order shown in the dropdown: roughly the lifecycle a coordinator thinks in,
 * not alphabetical. `legacy_preference` sits last because it is not a booking
 * state at all — it marks a candidate who predates the scheduler.
 */
const APPOINTMENT_STATUS_KEYS: AppointmentStatusKey[] = ['held', 'confirmed', 'completed', 'no_show', 'cancelled', 'legacy_preference']

const toValidIso = (date: Date | null): string | undefined => {
    if (!date || Number.isNaN(date.getTime())) return undefined
    return date.toISOString()
}

/**
 * Appointment dates are filtered on Eastern days, not browser days (APP-1720).
 *
 * The picker hands back a `Date` at the browser's local midnight, so sending it
 * straight through asked the API for a window in whatever zone the coordinator's
 * machine happened to be in — which never matched what the list displayed. These
 * turn the calendar day that was clicked into the instants that day begins and
 * ends in Eastern, so "3 Sep" means 3 Sep for the operations team regardless of
 * where the browser is, and lands on the right side of a daylight-saving change.
 */
const toEasternDayStartIso = (date: Date | null): string | undefined => {
    if (!date || Number.isNaN(date.getTime())) return undefined
    return pickedDayStart(date).toISOString()
}

const toEasternDayEndIso = (date: Date | null): string | undefined => {
    if (!date || Number.isNaN(date.getTime())) return undefined
    return pickedDayEnd(date).toISOString()
}

const isInvalidPreferredContactRange = (after: Date | null, before: Date | null): boolean => Boolean(after && before && after > before)

const syncDateRangeValue = (createdAfter?: string, createdBefore?: string): string => {
    if (!createdAfter || !createdBefore) return ''

    const afterMs = new Date(createdAfter).getTime()
    const beforeMs = new Date(createdBefore).getTime()
    if (Number.isNaN(afterMs) || Number.isNaN(beforeMs)) return ''

    const dayMs = 24 * 60 * 60 * 1000
    const diffDays = Math.round((beforeMs - afterMs) / dayMs)

    if (diffDays >= 0 && diffDays <= 2) return '1'
    if (diffDays >= 6 && diffDays <= 8) return '7'
    if (diffDays >= 29 && diffDays <= 31) return '30'

    return ''
}

const dateTimePickerTextFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: 'white'
    },
    '& .clearButton': {
        opacity: 1
    }
}

export const CandidateFilterComponent: React.FC<CandidateFilterComponentProps> = ({
    isOpen = false,
    onFiltersChange,
    currentFilters = {},
    onSearchChange,
    currentSearch = ''
}) => {
    const { user } = useAuth0()
    const { statusOptions: allStatusOptions, loading: statusOptionsLoading } = useStatusOptions()
    // The API serves every status the backend accepts; a study only offers its own.
    const statusOptions = React.useMemo(
        () => filterAllocatedStatusValues(allStatusOptions, user?.org_name as string | undefined),
        [allStatusOptions, user?.org_name]
    )
    const [statusValue, setStatusValue] = useState<string[]>(currentFilters.status || [])
    const [dateRangeValue, setDateRangeValue] = useState('')
    const [uncontactedOnly, setUncontactedOnly] = useState(currentFilters.uncontactedOnly || false)
    const [hasPreferredContactTime, setHasPreferredContactTime] = useState(
        currentFilters.hasPreferredContactTime === undefined ? '' : currentFilters.hasPreferredContactTime ? 'true' : 'false'
    )
    const [preferredContactAfter, setPreferredContactAfter] = useState<Date | null>(
        currentFilters.preferredContactAfter ? new Date(currentFilters.preferredContactAfter) : null
    )
    const [preferredContactBefore, setPreferredContactBefore] = useState<Date | null>(
        currentFilters.preferredContactBefore ? new Date(currentFilters.preferredContactBefore) : null
    )

    const preferredContactRangeError = isInvalidPreferredContactRange(preferredContactAfter, preferredContactBefore) ? t`End must be after start` : ''

    const [appointmentAfter, setAppointmentAfter] = useState<Date | null>(pickerDateFromZonedIso(currentFilters.appointmentAfter))
    const [appointmentBefore, setAppointmentBefore] = useState<Date | null>(pickerDateFromZonedIso(currentFilters.appointmentBefore))
    const [appointmentStatusValue, setAppointmentStatusValue] = useState<string[]>(currentFilters.appointmentStatus || [])
    const [hasAppointment, setHasAppointment] = useState(
        currentFilters.hasAppointment === undefined ? '' : currentFilters.hasAppointment ? 'true' : 'false'
    )
    const appointmentRangeError = isInvalidPreferredContactRange(appointmentAfter, appointmentBefore) ? t`End must be after start` : ''

    const DATE_RANGE_OPTIONS = [
        { value: '', label: t`All time` },
        { value: '1', label: t`Past 1 day` },
        { value: '7', label: t`Past 7 days` },
        { value: '30', label: t`Past 30 days` }
    ]

    // The `false` case is the useful one: it is how a coordinator finds
    // pre-scheduler registrations that still need a slot booking for them.
    const HAS_APPOINTMENT_OPTIONS = [
        { value: '', label: t`All` },
        { value: 'true', label: t`Has an appointment` },
        { value: 'false', label: t`No appointment yet` }
    ]

    const PREFERRED_CONTACT_TIME_OPTIONS = [
        { value: '', label: t`All` },
        { value: 'true', label: t`Has preferred contact time` },
        { value: 'false', label: t`No preferred contact time` }
    ]

    const wasOpenRef = useRef(false)

    useEffect(() => {
        const justOpened = isOpen && !wasOpenRef.current
        wasOpenRef.current = isOpen

        if (!justOpened) return

        setStatusValue(currentFilters.status || [])
        setDateRangeValue(syncDateRangeValue(currentFilters.createdAfter, currentFilters.createdBefore))
        setUncontactedOnly(currentFilters.uncontactedOnly || false)
        setHasPreferredContactTime(
            currentFilters.hasPreferredContactTime === undefined ? '' : currentFilters.hasPreferredContactTime ? 'true' : 'false'
        )
        setPreferredContactAfter(currentFilters.preferredContactAfter ? new Date(currentFilters.preferredContactAfter) : null)
        setAppointmentAfter(pickerDateFromZonedIso(currentFilters.appointmentAfter))
        setAppointmentBefore(pickerDateFromZonedIso(currentFilters.appointmentBefore))
        setAppointmentStatusValue(currentFilters.appointmentStatus || [])
        setHasAppointment(currentFilters.hasAppointment === undefined ? '' : currentFilters.hasAppointment ? 'true' : 'false')
        setPreferredContactBefore(currentFilters.preferredContactBefore ? new Date(currentFilters.preferredContactBefore) : null)
    }, [isOpen, currentFilters])

    const getDateRange = useCallback((days: string) => {
        if (!days) return { createdAfter: undefined, createdBefore: undefined }

        const now = new Date()
        const daysAgo = new Date(now.getTime() - parseInt(days, 10) * 24 * 60 * 60 * 1000)

        return {
            createdAfter: daysAgo.toISOString(),
            createdBefore: now.toISOString()
        }
    }, [])

    const buildFilters = useCallback(
        (
            overrides: {
                status?: string[]
                dateRange?: string
                uncontactedOnly?: boolean
                hasPreferredContactTime?: string
                preferredContactAfter?: Date | null
                preferredContactBefore?: Date | null
                appointmentAfter?: Date | null
                appointmentBefore?: Date | null
                appointmentStatus?: string[]
                hasAppointment?: string
            } = {}
        ): CandidateFilters => {
            const nextStatus = overrides.status !== undefined ? overrides.status : statusValue
            const nextDateRange = overrides.dateRange !== undefined ? overrides.dateRange : dateRangeValue
            const nextUncontactedOnly = overrides.uncontactedOnly !== undefined ? overrides.uncontactedOnly : uncontactedOnly
            const nextHasPreferredContactTime =
                overrides.hasPreferredContactTime !== undefined ? overrides.hasPreferredContactTime : hasPreferredContactTime
            const nextPreferredContactAfter = overrides.preferredContactAfter !== undefined ? overrides.preferredContactAfter : preferredContactAfter
            const nextPreferredContactBefore =
                overrides.preferredContactBefore !== undefined ? overrides.preferredContactBefore : preferredContactBefore

            const nextAppointmentAfter = overrides.appointmentAfter !== undefined ? overrides.appointmentAfter : appointmentAfter
            const nextAppointmentBefore = overrides.appointmentBefore !== undefined ? overrides.appointmentBefore : appointmentBefore
            const nextAppointmentStatus = overrides.appointmentStatus !== undefined ? overrides.appointmentStatus : appointmentStatusValue
            const nextHasAppointment = overrides.hasAppointment !== undefined ? overrides.hasAppointment : hasAppointment

            const contactRangeInvalid = isInvalidPreferredContactRange(nextPreferredContactAfter, nextPreferredContactBefore)
            const appointmentRangeInvalid = isInvalidPreferredContactRange(nextAppointmentAfter, nextAppointmentBefore)

            return {
                status: nextStatus.length > 0 ? nextStatus : undefined,
                ...getDateRange(nextDateRange),
                uncontactedOnly: nextUncontactedOnly || undefined,
                hasPreferredContactTime: nextHasPreferredContactTime === '' ? undefined : nextHasPreferredContactTime === 'true',
                preferredContactAfter: contactRangeInvalid ? undefined : toValidIso(nextPreferredContactAfter),
                preferredContactBefore: contactRangeInvalid ? undefined : toValidIso(nextPreferredContactBefore),
                // An invalid range sends nothing rather than a half-range: a
                // one-sided filter would silently return a different result set
                // from the one the coordinator is looking at.
                appointmentAfter: appointmentRangeInvalid ? undefined : toEasternDayStartIso(nextAppointmentAfter),
                appointmentBefore: appointmentRangeInvalid ? undefined : toEasternDayEndIso(nextAppointmentBefore),
                appointmentStatus: nextAppointmentStatus.length > 0 ? nextAppointmentStatus : undefined,
                hasAppointment: nextHasAppointment === '' ? undefined : nextHasAppointment === 'true'
            }
        },
        [
            appointmentAfter,
            appointmentBefore,
            appointmentStatusValue,
            hasAppointment,
            dateRangeValue,
            getDateRange,
            hasPreferredContactTime,
            preferredContactAfter,
            preferredContactBefore,
            statusValue,
            uncontactedOnly
        ]
    )

    const handleStatusChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            // MUI hands back a string when a native select fires; normalise so
            // downstream only ever deals with a list.
            const value = event.target.value
            const newStatus = typeof value === 'string' ? value.split(',').filter(Boolean) : value
            setStatusValue(newStatus)
            onFiltersChange(buildFilters({ status: newStatus }))
        },
        [buildFilters, onFiltersChange]
    )

    const handleDateRangeChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            const newDateRange = event.target.value
            setDateRangeValue(newDateRange)
            onFiltersChange(buildFilters({ dateRange: newDateRange }))
        },
        [buildFilters, onFiltersChange]
    )

    const handleUncontactedToggle = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newUncontactedOnly = event.target.checked
            setUncontactedOnly(newUncontactedOnly)
            onFiltersChange(buildFilters({ uncontactedOnly: newUncontactedOnly }))
        },
        [buildFilters, onFiltersChange]
    )

    // Same change/accept split as the preferred-contact pickers below, and for
    // the same reason: a DateTimePicker emits `onChange` for every intermediate
    // edit, so emitting there would fire a query per keystroke — mostly on
    // half-typed dates. Only `onAccept` (and an explicit clear) is a real intent.
    const handleAppointmentAfterAccept = useCallback(
        (value: Date | null) => {
            setAppointmentAfter(value)
            if (isInvalidPreferredContactRange(value, appointmentBefore)) return
            onFiltersChange(buildFilters({ appointmentAfter: value }))
        },
        [buildFilters, onFiltersChange, appointmentBefore]
    )

    const handleAppointmentBeforeAccept = useCallback(
        (value: Date | null) => {
            setAppointmentBefore(value)
            if (isInvalidPreferredContactRange(appointmentAfter, value)) return
            onFiltersChange(buildFilters({ appointmentBefore: value }))
        },
        [buildFilters, onFiltersChange, appointmentAfter]
    )

    const handleAppointmentAfterChange = useCallback(
        (value: Date | null) => {
            const wasCleared = appointmentAfter !== null && value === null
            setAppointmentAfter(value)
            // Clearing needs no accept — the picker's clear button is the intent.
            if (wasCleared) {
                onFiltersChange(buildFilters({ appointmentAfter: null }))
            }
        },
        [buildFilters, onFiltersChange, appointmentAfter]
    )

    const handleAppointmentBeforeChange = useCallback(
        (value: Date | null) => {
            const wasCleared = appointmentBefore !== null && value === null
            setAppointmentBefore(value)
            if (wasCleared) {
                onFiltersChange(buildFilters({ appointmentBefore: null }))
            }
        },
        [buildFilters, onFiltersChange, appointmentBefore]
    )

    const handleAppointmentStatusChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            // Same MUI quirk as the candidate status filter: a native select
            // fires a string rather than a list.
            const value = event.target.value
            const next = typeof value === 'string' ? value.split(',').filter(Boolean) : value
            setAppointmentStatusValue(next)
            onFiltersChange(buildFilters({ appointmentStatus: next }))
        },
        [buildFilters, onFiltersChange]
    )

    const handleHasAppointmentChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            const value = event.target.value
            setHasAppointment(value)
            onFiltersChange(buildFilters({ hasAppointment: value }))
        },
        [buildFilters, onFiltersChange]
    )

    const handlePreferredContactTimeChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            const value = event.target.value
            setHasPreferredContactTime(value)
            onFiltersChange(buildFilters({ hasPreferredContactTime: value }))
        },
        [buildFilters, onFiltersChange]
    )

    const handlePreferredContactAfterAccept = useCallback(
        (value: Date | null) => {
            setPreferredContactAfter(value)
            if (isInvalidPreferredContactRange(value, preferredContactBefore)) return
            onFiltersChange(buildFilters({ preferredContactAfter: value }))
        },
        [buildFilters, onFiltersChange, preferredContactBefore]
    )

    const handlePreferredContactBeforeAccept = useCallback(
        (value: Date | null) => {
            setPreferredContactBefore(value)
            if (isInvalidPreferredContactRange(preferredContactAfter, value)) return
            onFiltersChange(buildFilters({ preferredContactBefore: value }))
        },
        [buildFilters, onFiltersChange, preferredContactAfter]
    )

    const handlePreferredContactAfterChange = useCallback(
        (value: Date | null) => {
            const wasCleared = preferredContactAfter !== null && value === null
            setPreferredContactAfter(value)
            if (wasCleared) {
                onFiltersChange(buildFilters({ preferredContactAfter: null }))
            }
        },
        [buildFilters, onFiltersChange, preferredContactAfter]
    )

    const handlePreferredContactBeforeChange = useCallback(
        (value: Date | null) => {
            const wasCleared = preferredContactBefore !== null && value === null
            setPreferredContactBefore(value)
            if (wasCleared) {
                onFiltersChange(buildFilters({ preferredContactBefore: null }))
            }
        },
        [buildFilters, onFiltersChange, preferredContactBefore]
    )

    return (
        <div className='flex flex-col gap-4'>
            {/* Row 1: Search */}
            <div className='w-full'>
                <StableSearchInput
                    onSearchChange={onSearchChange || (() => {})}
                    initialValue={currentSearch}
                    placeholder={t`Search by name, email, or identifier`}
                />
            </div>

            {/* Row 2: Status Filter */}
            <div className='w-full'>
                <FormControl
                    fullWidth
                    size='small'
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'white'
                        }
                    }}
                >
                    <InputLabel id='status-filter-label'>{t`Filter Status`}</InputLabel>
                    <Select
                        multiple
                        id='status-filter-select'
                        labelId='status-filter-label'
                        label={t`Filter Status`}
                        inputProps={{ 'data-testid': 'status-filter-input' }}
                        value={statusValue}
                        onChange={handleStatusChange}
                        disabled={statusOptionsLoading}
                        renderValue={(selected) =>
                            selected.length === 0 ? (
                                <em>{t`All`}</em>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((status) => (
                                        <StatusBadge key={status} status={status} />
                                    ))}
                                </Box>
                            )
                        }
                    >
                        {statusOptions.map((status) => (
                            <MenuItem key={status} value={status} data-testid={`status-option-${status}`}>
                                <Checkbox checked={statusValue.includes(status)} size='small' />
                                <ListItemText disableTypography primary={<StatusBadge status={status} />} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {/* Row 3: Date Range Filter */}
            <div className='w-full'>
                <FormControl
                    fullWidth
                    size='small'
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'white'
                        }
                    }}
                >
                    <InputLabel id='date-range-filter-label'>{t`Created`}</InputLabel>
                    <Select
                        id='date-range-filter-select'
                        labelId='date-range-filter-label'
                        label={t`Created`}
                        inputProps={{ 'data-testid': 'date-range-filter-input' }}
                        value={dateRangeValue}
                        onChange={handleDateRangeChange}
                    >
                        {DATE_RANGE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {/* Appointment window + status (APP-1220). Placed above the legacy
                preferred-contact controls because the appointment is the live
                thing a coordinator works from; the window below only still
                matters for candidates who predate the scheduler. */}
            <div className='w-full space-y-3'>
                <p className='text-sm text-gray-600 m-0'>{t`Appointment date (Eastern Time)`}</p>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label={t`From`}
                        value={appointmentAfter}
                        onChange={handleAppointmentAfterChange}
                        onAccept={handleAppointmentAfterAccept}
                        closeOnSelect={false}
                        slotProps={{
                            field: { clearable: true },
                            actionBar: { actions: ['clear', 'cancel', 'accept'] },
                            textField: {
                                fullWidth: true,
                                size: 'small',
                                error: Boolean(appointmentRangeError),
                                inputProps: { 'data-testid': 'appointment-after-input' },
                                sx: dateTimePickerTextFieldSx
                            }
                        }}
                    />
                    <DatePicker
                        label={t`To`}
                        value={appointmentBefore}
                        onChange={handleAppointmentBeforeChange}
                        onAccept={handleAppointmentBeforeAccept}
                        closeOnSelect={false}
                        slotProps={{
                            field: { clearable: true },
                            actionBar: { actions: ['clear', 'cancel', 'accept'] },
                            textField: {
                                fullWidth: true,
                                size: 'small',
                                error: Boolean(appointmentRangeError),
                                helperText: appointmentRangeError || undefined,
                                inputProps: { 'data-testid': 'appointment-before-input' },
                                sx: dateTimePickerTextFieldSx
                            }
                        }}
                    />
                </LocalizationProvider>
            </div>

            {/* Appointment status — multi-select, so "confirmed and completed"
                is one query rather than two passes through the list. */}
            <div className='w-full'>
                <FormControl fullWidth size='small' sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: 'white' } }}>
                    <InputLabel id='appointment-status-filter-label'>{t`Appointment Status`}</InputLabel>
                    <Select
                        labelId='appointment-status-filter-label'
                        multiple
                        value={appointmentStatusValue}
                        onChange={handleAppointmentStatusChange}
                        label={t`Appointment Status`}
                        inputProps={{ 'data-testid': 'appointment-status-filter' }}
                        renderValue={(selected) =>
                            (selected as string[]).map((v) => APPOINTMENT_STATUS_LABELS[v as AppointmentStatusKey] ?? v).join(', ')
                        }
                    >
                        {APPOINTMENT_STATUS_KEYS.map((key) => (
                            <MenuItem key={key} value={key}>
                                {APPOINTMENT_STATUS_LABELS[key]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {/* Appointment presence. Separate from the status multi-select
                because "no appointment at all" is not a status — those rows
                have no appointment record to carry one. */}
            <div className='w-full'>
                <FormControl fullWidth size='small' sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: 'white' } }}>
                    <InputLabel id='has-appointment-filter-label'>{t`Appointment`}</InputLabel>
                    <Select
                        id='has-appointment-filter-select'
                        labelId='has-appointment-filter-label'
                        label={t`Appointment`}
                        inputProps={{ 'data-testid': 'has-appointment-filter-input' }}
                        value={hasAppointment}
                        onChange={handleHasAppointmentChange}
                    >
                        {HAS_APPOINTMENT_OPTIONS.map((option) => (
                            <MenuItem key={option.value || 'all'} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {/* Row 4: Preferred Contact Window */}
            <div className='w-full space-y-3'>
                <p className='text-sm text-gray-600 m-0'>{t`Preferred contact window overlaps`}</p>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                        label={t`From`}
                        value={preferredContactAfter}
                        onChange={handlePreferredContactAfterChange}
                        onAccept={handlePreferredContactAfterAccept}
                        closeOnSelect={false}
                        slotProps={{
                            field: { clearable: true },
                            actionBar: { actions: ['clear', 'cancel', 'accept'] },
                            textField: {
                                fullWidth: true,
                                size: 'small',
                                error: Boolean(preferredContactRangeError),
                                inputProps: { 'data-testid': 'preferred-contact-after-input' },
                                sx: dateTimePickerTextFieldSx
                            }
                        }}
                    />
                    <DateTimePicker
                        label={t`To`}
                        value={preferredContactBefore}
                        onChange={handlePreferredContactBeforeChange}
                        onAccept={handlePreferredContactBeforeAccept}
                        closeOnSelect={false}
                        slotProps={{
                            field: { clearable: true },
                            actionBar: { actions: ['clear', 'cancel', 'accept'] },
                            textField: {
                                fullWidth: true,
                                size: 'small',
                                error: Boolean(preferredContactRangeError),
                                helperText: preferredContactRangeError,
                                inputProps: { 'data-testid': 'preferred-contact-before-input' },
                                sx: dateTimePickerTextFieldSx
                            }
                        }}
                    />
                </LocalizationProvider>
            </div>

            {/* Row 5: Preferred Contact Time Filter */}
            <div className='w-full'>
                <FormControl
                    fullWidth
                    size='small'
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'white'
                        }
                    }}
                >
                    <InputLabel id='preferred-contact-time-filter-label'>{t`Preferred Contact Time`}</InputLabel>
                    <Select
                        id='preferred-contact-time-filter-select'
                        labelId='preferred-contact-time-filter-label'
                        label={t`Preferred Contact Time`}
                        inputProps={{ 'data-testid': 'preferred-contact-time-filter-input' }}
                        value={hasPreferredContactTime}
                        onChange={handlePreferredContactTimeChange}
                    >
                        {PREFERRED_CONTACT_TIME_OPTIONS.map((option) => (
                            <MenuItem key={option.value || 'all'} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {/* Row 6: Uncontacted Toggle */}
            <div className='w-full'>
                <FormControlLabel
                    control={
                        <Switch
                            checked={uncontactedOnly}
                            onChange={handleUncontactedToggle}
                            inputProps={{ 'data-testid': 'uncontacted-filter-toggle' } as any}
                            color='primary'
                        />
                    }
                    label={t`Show only uncontacted`}
                    sx={{
                        '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                            color: 'text.secondary'
                        }
                    }}
                />
            </div>
        </div>
    )
}

export default CandidateFilterComponent
